import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, AlertTriangle, Car } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryState, parseAsString, createParser } from 'nuqs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { VehicleDataTable } from './VehicleDataTable';
import { columns, editVehicleEvent, deleteVehicleEvent, createJobCardEvent } from './columns';
import { listVehicles, deleteVehicle } from '@/app/services/vehicleService';
import { VehicleListItem } from '@/app/types/vehicle';
import { useOrganization } from '@/app/hooks/useOrganization';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';
import { SortingState } from '@tanstack/react-table';

// Extended PaginatedResponse with page count
interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number;
  pageCount?: number;
  total_pages?: number;
  totalPages?: number;
}

type SortDirection = 'asc' | 'desc';

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return [{ id: 'registration_number', desc: false }];
    const [field, direction] = value.split('-');
    return [
      {
        id: field || 'registration_number',
        desc: direction === 'desc',
      },
    ];
  },
  serialize: (value: SortingState) => {
    if (!value.length) return 'registration_number-asc';
    const sort = value[0];
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`;
  },
});

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10);
    return isNaN(page) || page < 1 ? 0 : page - 1;
  },
  serialize: (value: number) => {
    return String(value + 1);
  },
});

// Custom parser for filters
type FilterValue = string | number | boolean | { from?: string; to?: string };

const filtersParser = createParser({
  parse: (value: string): Record<string, FilterValue> => {
    try {
      return value ? JSON.parse(decodeURIComponent(value)) : {};
    } catch {
      return {};
    }
  },
  serialize: (value: Record<string, FilterValue>) => {
    return Object.keys(value).length > 0 ? encodeURIComponent(JSON.stringify(value)) : '';
  },
});

const VehicleListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const navigate = useNavigate();

  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0));
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''));
  const [sorting, setSorting] = useQueryState(
    'sort',
    sortingParser.withDefault([{ id: 'registration_number', desc: false }])
  );
  const [filters, setFilters] = useQueryState('filters', filtersParser.withDefault({}));
  const [limit] = useState<number>(10);

  // Local state for dialogs
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleListItem | null>(null);

  // Determine sort parameters for API call
  const getSortParams = useCallback(() => {
    if (sorting.length === 0)
      return {
        sort_by: 'registration_number',
        sort_dir: 'asc' as SortDirection,
      };

    const sort = sorting[0];
    const direction: SortDirection = sort.desc ? 'desc' : 'asc';

    return {
      sort_by: sort.id,
      sort_dir: direction,
    };
  }, [sorting]);

  // Build filter parameters for API
  const buildFilterParams = (
    filters: Record<string, string | number | boolean | { from?: string; to?: string }>
  ): string => {
    const filterArray: { field: string; value: unknown; op: string }[] = [];

    Object.entries(filters).forEach(([field, value]) => {
      if (value === undefined || value === null || value === '') return;

      // Handle text fields with contains search
      if (
        field === 'registration_number' ||
        field === 'make' ||
        field === 'model' ||
        field === 'owner.name'
      ) {
        filterArray.push({
          field: field,
          value: `%${value}%`,
          op: 'ilike',
        });
        return;
      }

      // Handle exact match fields (fuel_type, etc.)
      filterArray.push({
        field: field,
        value: String(value),
        op: 'eq',
      });
    });

    return JSON.stringify(filterArray);
  };

  // Handle filter changes
  const handleFilterChange = useCallback(
    (
      newFilters: Record<string, string | number | boolean | { from?: string; to?: string }>
    ): void => {
      setFilters(newFilters);
      setPage(0);
    },
    [setFilters, setPage]
  );

  // Fetch vehicles with search and pagination
  const {
    data: vehiclesData,
    isLoading,
    isError,
    error,
  } = useQuery<ExtendedPaginatedResponse<VehicleListItem>>({
    queryKey: ['vehicles', page, limit, search, sorting, filters, currentOrganizationId],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams();
      const skip = page * limit;

      const params: ListQueryParams = {
        skip,
        limit,
        sort_by,
        sort_dir,
      };

      if (search) {
        params.search_query = search;
      }

      if (Object.keys(filters).length > 0) {
        params.filters = buildFilterParams(filters);
      }

      return listVehicles(params);
    },
    enabled: !!currentOrganizationId,
  });

  // Calculate page count
  const pageCount =
    vehiclesData?.page_count ??
    vehiclesData?.pageCount ??
    vehiclesData?.total_pages ??
    vehiclesData?.totalPages ??
    (vehiclesData ? Math.ceil(vehiclesData.total / limit) : 0);

  // Delete vehicle mutation
  const deleteVehicleMutation = useMutation({
    mutationFn: (id: number) => deleteVehicle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      setIsDeleteAlertOpen(false);
      setSelectedVehicle(null);
      toast.success('Vehicle deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete vehicle: ${error.message}`);
    },
  });

  // Event handlers for table actions
  React.useEffect(() => {
    const handleEditVehicle = (event: CustomEvent<{ vehicle: VehicleListItem }>): void => {
      navigate(`/workshop/vehicles/detail/${event.detail.vehicle.id}`);
    };

    const handleDeleteVehicle = (event: CustomEvent<{ vehicle: VehicleListItem }>): void => {
      setSelectedVehicle(event.detail.vehicle);
      setIsDeleteAlertOpen(true);
    };

    const handleCreateJobCard = (event: CustomEvent<{ vehicle: VehicleListItem }>): void => {
      navigate(`/workshop/job-cards/detail?vehicle_id=${event.detail.vehicle.id}`);
    };

    document.addEventListener(editVehicleEvent, handleEditVehicle as EventListener);
    document.addEventListener(deleteVehicleEvent, handleDeleteVehicle as EventListener);
    document.addEventListener(createJobCardEvent, handleCreateJobCard as EventListener);

    return () => {
      document.removeEventListener(editVehicleEvent, handleEditVehicle as EventListener);
      document.removeEventListener(deleteVehicleEvent, handleDeleteVehicle as EventListener);
      document.removeEventListener(createJobCardEvent, handleCreateJobCard as EventListener);
    };
  }, [navigate]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string): void => {
      setSearch(value);
      setPage(0);
    },
    [setSearch, setPage]
  );

  // Handle sorting change
  const handleSortingChange = useCallback(
    (newSorting: SortingState): void => {
      setSorting(newSorting);
      setPage(0);
    },
    [setSorting, setPage]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number): void => {
      setPage(newPage);
    },
    [setPage]
  );

  // Handle delete confirmation
  const handleDeleteVehicle = (): void => {
    if (selectedVehicle) {
      deleteVehicleMutation.mutate(selectedVehicle.id);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Car className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Vehicles</h1>
        </div>
        <Button asChild>
          <Link to="/workshop/vehicles/detail">
            <Plus className="h-4 w-4 mr-2" />
            Register Vehicle
          </Link>
        </Button>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Error loading vehicles: {(error as Error).message}</AlertDescription>
        </Alert>
      )}

      {/* Vehicles Data Table */}
      <VehicleDataTable
        data={vehiclesData?.items || []}
        columns={columns}
        isLoading={isLoading}
        pageCount={pageCount}
        page={page}
        setPage={handlePageChange}
        onSearchChange={handleSearchChange}
        searchValue={search}
        onSortingChange={handleSortingChange}
        sortingState={sorting}
        onFilterChange={handleFilterChange}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete vehicle{' '}
              <span className="font-semibold">{selectedVehicle?.registration_number}</span>? This
              action cannot be undone.
              <br />
              <br />
              <span className="text-destructive">
                Note: Vehicles with existing job cards cannot be deleted.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteVehicle}
              disabled={deleteVehicleMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteVehicleMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VehicleListPage;
